import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { z } from "zod";
import { useProjectContext } from "~/components/layout/project-context";
import { DeduplicationSettings } from "~/components/settings/deduplication-settings";
import { DualScreeningSettings } from "~/components/settings/dual-screening-settings";
import { ExclusionCodesTab } from "~/components/settings/exclusion-codes-tab";
import { ReviewSettingsTab } from "~/components/settings/review-settings-tab";
import { ReviewersTab } from "~/components/settings/reviewers-tab";
import { projectQueryOptions } from "~/lib/projects/queries";
import { screeningSettingsQueryOptions } from "~/lib/screening/queries";
import {
  projectExclusionCodesQueryOptions,
  projectInvitationsQueryOptions,
  projectMembersQueryOptions,
} from "~/lib/settings/queries";

const settingsSearchSchema = z.object({
  tab: z
    .enum(["review", "reviewers", "exclusion-codes", "dual-screening", "deduplication"])
    .default("review"),
});

export const Route = createFileRoute("/_auth/projects/$projectId/settings")({
  validateSearch: settingsSearchSchema,
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(projectQueryOptions(params.projectId)),
      context.queryClient.ensureQueryData(projectMembersQueryOptions(params.projectId)),
      context.queryClient.ensureQueryData(
        projectInvitationsQueryOptions(params.projectId),
      ),
      context.queryClient.ensureQueryData(
        projectExclusionCodesQueryOptions(params.projectId),
      ),
      context.queryClient.ensureQueryData(
        screeningSettingsQueryOptions(params.projectId),
      ),
    ]);
  },
  component: ProjectSettingsPage,
});

function ProjectSettingsPage() {
  const { projectId, projectTitle, isOwner } =
    useProjectContext();
  const { tab } = Route.useSearch();
  const { data: project } = useSuspenseQuery(projectQueryOptions(projectId));

  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1 p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold">Project Settings</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Manage your project settings and team members.
          </p>
        </div>

        <div className="mb-6 flex gap-1 overflow-x-auto border-b">
          <Link
            to="/projects/$projectId/settings"
            params={{ projectId }}
            search={{ tab: "review" }}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              tab === "review"
                ? "border-foreground text-foreground border-b-2"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Review Settings
          </Link>
          <Link
            to="/projects/$projectId/settings"
            params={{ projectId }}
            search={{ tab: "reviewers" }}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              tab === "reviewers"
                ? "border-foreground text-foreground border-b-2"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Reviewers
          </Link>
          <Link
            to="/projects/$projectId/settings"
            params={{ projectId }}
            search={{ tab: "exclusion-codes" }}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              tab === "exclusion-codes"
                ? "border-foreground text-foreground border-b-2"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Exclusion Codes
          </Link>
          <Link
            to="/projects/$projectId/settings"
            params={{ projectId }}
            search={{ tab: "dual-screening" }}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              tab === "dual-screening"
                ? "border-foreground text-foreground border-b-2"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Dual Screening
          </Link>
          <Link
            to="/projects/$projectId/settings"
            params={{ projectId }}
            search={{ tab: "deduplication" }}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              tab === "deduplication"
                ? "border-foreground text-foreground border-b-2"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Deduplication
          </Link>
        </div>

        <div className="max-w-2xl">
          {tab === "review" && <ReviewSettingsTab project={project} isOwner={isOwner} />}
          {tab === "reviewers" && (
            <Suspense
              fallback={
                <div className="text-muted-foreground py-8 text-center">
                  Loading team...
                </div>
              }
            >
              <ReviewersTab projectId={projectId} isOwner={isOwner} />
            </Suspense>
          )}
          {tab === "exclusion-codes" && (
            <Suspense
              fallback={
                <div className="text-muted-foreground py-8 text-center">
                  Loading exclusion codes...
                </div>
              }
            >
              <ExclusionCodesTab projectId={projectId} isOwner={isOwner} />
            </Suspense>
          )}
          {tab === "dual-screening" && (
            <Suspense
              fallback={
                <div className="text-muted-foreground py-8 text-center">
                  Loading dual screening settings...
                </div>
              }
            >
              <DualScreeningSettings projectId={projectId} isOwner={isOwner} />
            </Suspense>
          )}
          {tab === "deduplication" && (
            <Suspense
              fallback={
                <div className="text-muted-foreground py-8 text-center">
                  Loading deduplication settings...
                </div>
              }
            >
              <DeduplicationSettings projectId={projectId} isOwner={isOwner} />
            </Suspense>
          )}
        </div>
      </main>
    </div>
  );
}
